// @ts-check
// Catálogo "Blocos" do painel lateral do editor de bots (contrato de bots, seção 22.2), como o catálogo dos fluxos:
// busca, sanfonas por categoria (Mensagens, Caminho, Identificação, Atendimento, CRM, Integrações; estado guardado no
// navegador) e um cartão por bloco, que se arrasta até um "+" do bot ou entra no ponto escolhido com clique, Enter ou
// Espaço. Bloco que não cabe no ponto fica desabilitado com a explicação.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { BOT_CATALOG_CATEGORIES, BOT_CATALOG_ITEMS, botCategoryStyle } from '../../lib/bot-catalog.js';
import { FLOW_ACTION_DRAG_TYPE } from '../flows/flow-connector.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../lib/bot-catalog.js').BotCatalogItem} BotCatalogItem */

const CLOSED_STORAGE_KEY = 'appcrm.botEditor.catalogClosed';

/** @returns {string[]} */
function readClosedCategories() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CLOSED_STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} closed */
function storeClosedCategories(closed) {
  try {
    window.localStorage.setItem(CLOSED_STORAGE_KEY, JSON.stringify(closed));
  } catch {
    // sem armazenamento: as sanfonas voltam abertas na próxima vez
  }
}

/** Blocos que batem com a busca (nome do bloco e da categoria, sem acento). @param {string} search @returns {BotCatalogItem[]} */
export function searchBotCatalog(search) {
  const needle = normalizeSearchText(search);
  if (!needle) return [...BOT_CATALOG_ITEMS];
  return BOT_CATALOG_ITEMS.filter((item) => normalizeSearchText(`${t(item.nameKey)} ${t(`bots.catalog.categories.${item.category}`)}`).includes(needle));
}

/**
 * @param {{ insertLabel?: string | null, disabledReason: (item: BotCatalogItem) => string | null, onPick: (item: BotCatalogItem, viaKeyboard: boolean) => void,
 *   onDragStart?: (item: BotCatalogItem) => void, onDragEnd?: () => void, canDrag?: boolean }} props
 */
export function BotBlockCatalog({ insertLabel = null, disabledReason, onPick, onDragStart, onDragEnd, canDrag = true }) {
  const [search, setSearch] = useState('');
  const [closed, setClosed] = useState(readClosedCategories);
  const items = searchBotCatalog(search);
  /** @param {string} category */
  const toggle = (category) => {
    const next = closed.includes(category) ? closed.filter((value) => value !== category) : [...closed, category];
    setClosed(next);
    storeClosedCategories(next);
  };
  return html`<div class="flow-catalog" data-testid="bot-block-catalog">
    ${insertLabel ? html`<p class="flow-catalog-insert-point"><${Icon} name="IconPlus" size=${14} /><span>${insertLabel}</span></p>` : null}
    <p class="flow-catalog-help">${canDrag ? t('bots.catalog.help') : t('bots.catalog.helpTouch')}</p>
    <label class="flows-search flow-catalog-search">
      <${Icon} name="IconSearch" size=${16} />
      <input type="search" class="flows-search-input" value=${search} placeholder=${t('bots.catalog.search')} aria-label=${t('bots.catalog.search')}
        onInput=${(/** @type {InputEvent} */ event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
    </label>
    ${items.length === 0 ? html`<p class="flow-catalog-empty">${t('bots.catalog.noResults', { query: search })}</p>` : null}
    ${BOT_CATALOG_CATEGORIES.map((category) => {
      const categoryItems = items.filter((item) => item.category === category);
      if (categoryItems.length === 0) return null;
      const isOpen = Boolean(search.trim()) || !closed.includes(category);
      const regionId = `bot-catalog-${category}`;
      return html`<section key=${category} class="flow-catalog-section">
        <h3 class="flow-catalog-heading">
          <button type="button" class="flow-catalog-toggle" aria-expanded=${isOpen ? 'true' : 'false'} aria-controls=${regionId} onClick=${() => toggle(category)}>
            <span>${t(`bots.catalog.categories.${category}`)}</span>
            <${Icon} name=${isOpen ? 'IconChevronUp' : 'IconChevronDown'} size=${14} />
          </button>
        </h3>
        ${isOpen
          ? html`<ul class="flow-catalog-items" id=${regionId} role="list">
              ${categoryItems.map((item) => {
                const reason = disabledReason(item);
                return html`<li key=${item.key}>
                  <div class="flow-catalog-item" role="button" tabindex=${reason ? -1 : 0} aria-disabled=${reason ? 'true' : undefined}
                    title=${reason ?? t(`bots.catalog.descriptions.${item.key}`)} draggable=${canDrag && !reason ? 'true' : 'false'} data-testid=${`bot-catalog-${item.key}`}
                    aria-label=${reason ? `${t(item.nameKey)}. ${reason}` : t(item.nameKey)}
                    onClick=${(/** @type {MouseEvent} */ event) => !reason && onPick(item, event.detail === 0)}
                    onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
                      if ((event.key === 'Enter' || event.key === ' ') && !reason) {
                        event.preventDefault();
                        onPick(item, true);
                      }
                    }}
                    onDragStart=${(/** @type {DragEvent} */ event) => {
                      if (reason || !event.dataTransfer) return;
                      event.dataTransfer.effectAllowed = 'copy';
                      event.dataTransfer.setData(FLOW_ACTION_DRAG_TYPE, item.key);
                      event.dataTransfer.setData('text/plain', item.key);
                      onDragStart?.(item);
                    }}
                    onDragEnd=${() => onDragEnd?.()}>
                    <span class="flow-category-circle" data-size="24" style=${botCategoryStyle(item.type)} aria-hidden="true"><${Icon} name=${item.icon} size=${14} /></span>
                    <span class="flow-catalog-item-name">${t(item.nameKey)}</span>
                    ${canDrag ? html`<span class="flow-catalog-grip" aria-hidden="true"><${Icon} name="IconGripVertical" size=${16} /></span>` : null}
                  </div>
                </li>`;
              })}
            </ul>`
          : null}
      </section>`;
    })}
  </div>`;
}
